'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Eye,
  EyeOff,
  Lock,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { authClient } from '@/lib/auth/client';

const ease = [0.22, 1, 0.36, 1] as const;

interface SetNewPasswordFormProps {
  token: string;
}

export function SetNewPasswordForm({ token }: SetNewPasswordFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const validate = (): boolean => {
    const errors: { newPassword?: string; confirmPassword?: string } = {};
    if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters.';
    }
    if (confirmPassword !== newPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!validate()) return;

    setIsLoading(true);

    try {
      const { error: authError } = await authClient.resetPassword({
        newPassword,
        token,
      });

      if (authError) {
        if (
          authError.code === 'INVALID_TOKEN' ||
          authError.code === 'TOKEN_NOT_FOUND' ||
          authError.status === 400
        ) {
          setError('Invalid reset link. Please request a new one.');
        } else if (
          authError.code === 'TOKEN_EXPIRED' ||
          authError.status === 410
        ) {
          setError('This reset link has expired. Please request a new one.');
        } else {
          setError('Something went wrong. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      setSuccess(true);
    } catch {
      setError(
        'Unable to connect. Check your internet connection and try again.'
      );
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease }}
        className="w-full space-y-6"
      >
        {/* Mobile logo */}
        <div className="lg:hidden mb-10">
          <Image
            src="https://assets.getcreatr.com/uploads/1778677644595-4d8s0hnts6u-image.png"
            alt="Everspark"
            width={130}
            height={30}
            className="h-7 w-auto"
            priority
          />
        </div>

        <div className="flex flex-col gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-6 w-6 text-success" />
          </div>
          <div>
            <h2 className="font-display italic text-[2rem] text-foreground leading-tight">
              Password updated
            </h2>
            <p className="mt-2 text-[15px] text-muted-foreground">
              Your password has been updated successfully.
            </p>
          </div>
        </div>

        <Button
          asChild
          className="w-full h-11 rounded-lg text-sm font-sans font-medium tracking-wide"
        >
          <a href="/login">Sign In</a>
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      className="w-full"
    >
      {/* Mobile logo */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5, ease }}
        className="lg:hidden mb-10"
      >
        <Image
          src="https://assets.getcreatr.com/uploads/1778677644595-4d8s0hnts6u-image.png"
          alt="Everspark"
          width={130}
          height={30}
          className="h-7 w-auto"
          priority
        />
      </motion.div>

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.6, ease }}
        className="mb-8"
      >
        <h2 className="font-display italic text-[2rem] lg:text-[2.25rem] text-foreground leading-tight">
          Set new password
        </h2>
        <p className="mt-1.5 text-muted-foreground text-[15px]">
          Choose a strong password for your account
        </p>
      </motion.div>

      {/* Error banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/8 px-4 py-3"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="flex-1">
            <p className="text-[13px] text-destructive leading-snug">{error}</p>
            {(error.includes('expired') || error.includes('Invalid')) && (
              <a
                href="/reset-password"
                className="mt-1 inline-block text-[12px] text-destructive/80 hover:text-destructive underline-offset-2 hover:underline transition-colors"
              >
                Request a new reset link
              </a>
            )}
          </div>
        </motion.div>
      )}

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6, ease }}
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* New Password */}
        <div className="space-y-2">
          <Label
            htmlFor="new-password"
            className="text-xs font-sans font-medium tracking-wider uppercase text-muted-foreground"
          >
            New Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground/50 pointer-events-none" />
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="h-11 pl-10 pr-10 rounded-lg bg-card text-sm placeholder:text-muted-foreground/40"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {fieldErrors.newPassword && (
            <p className="text-[12px] text-destructive">
              {fieldErrors.newPassword}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label
            htmlFor="confirm-password"
            className="text-xs font-sans font-medium tracking-wider uppercase text-muted-foreground"
          >
            Confirm Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground/50 pointer-events-none" />
            <Input
              id="confirm-password"
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your new password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-11 pl-10 pr-10 rounded-lg bg-card text-sm placeholder:text-muted-foreground/40"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
              tabIndex={-1}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {fieldErrors.confirmPassword && (
            <p className="text-[12px] text-destructive">
              {fieldErrors.confirmPassword}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 rounded-lg text-sm font-sans font-medium tracking-wide"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Setting new password...
            </span>
          ) : (
            'Set New Password'
          )}
        </Button>
      </motion.form>

      {/* Back to sign in */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="mt-6 text-center"
      >
        <a
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </a>
      </motion.div>
    </motion.div>
  );
}
